import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#8b5cf6';
  const lastUpdated = 'January 22, 2026';

  return (
    <LegalLayout app="Prismatica" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlab ("we," "our," or "us") operates the Prismatica mobile application
                (the "App"). Prismatica is a professional camera app designed to give you
                complete control over your photography and videography with manual controls
                and advanced features.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use our App.
                We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                Prismatica is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Zero Third-Party SDKs:</strong> We use only Apple's native frameworks. No analytics, tracking, or advertising SDKs are included.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration or login.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics or behavioral data.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Processing:</strong> All camera processing, filters, and effects happen on your device.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Permissions We Request
              </h2>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Camera Access (Required)
              </h3>
              <p className="mb-4 leading-relaxed">
                Prismatica needs camera access to capture professional photos and videos with
                manual controls and advanced features. Without camera access, the app cannot
                perform its primary function.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Microphone Access (Required for Video)
              </h3>
              <p className="mb-4 leading-relaxed">
                For high-quality video recording with synchronized audio, Prismatica needs
                microphone access. You can still capture photos without granting this permission.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Photo Library Access (Required)
              </h3>
              <p className="mb-4 leading-relaxed">
                Prismatica needs access to your photo library to save your captured photos
                and videos. We also use this to display your recent captures in the gallery view.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Location Access (Optional)
              </h3>
              <p className="mb-4 leading-relaxed">
                If you grant location permission, Prismatica can add GPS coordinates to your
                photos and videos for better organization. This is completely optional and
                location data is stored only in your media files.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Motion Data (Optional)
              </h3>
              <p className="leading-relaxed">
                Prismatica can use motion sensors to enhance image stabilization and provide
                level indicators. This data is processed locally and never transmitted.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage & Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data and settings are protected:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All app settings, preferences, and panel configurations are stored locally on your device.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Photo Library:</strong> Captured media is saved directly to your device's Photos app.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Cloud Upload:</strong> Prismatica never uploads your photos, videos, or settings to any external server.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>iOS Encryption:</strong> Your data benefits from iOS's built-in device encryption.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                Prismatica does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Your photos or videos</li>
                <li>Camera usage patterns</li>
                <li>Location history</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
                <li>Any data to external servers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                Prismatica uses <strong style={{ color: 'var(--text-primary)' }}>only Apple's native frameworks</strong>:
                SwiftUI, AVFoundation, Core Image, Photos, Core Location, Core Motion, and StoreKit.
                We do not integrate any third-party SDKs, analytics tools, crash reporters, or
                advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights & Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Permissions:</strong> Revoke any permission at any time in iOS Settings.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Photos:</strong> Manage and delete captured media through the Photos app.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Settings:</strong> Reset all app settings from within Prismatica.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored preferences.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Prismatica is not directed at children under the age of 13. We do not knowingly
                collect personal information from children. Since we don't collect any personal
                data, no special protections are needed.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:tanushgupta@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    tanushgupta@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/prismatica/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Prismatica Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
